import { TZDate, TZDateMini } from '@date-fns/tz';
import {
  addSeconds,
  constructFrom,
  ContextFn,
  DateArg,
  EachDayOfIntervalOptions,
  endOfDay,
  isBefore,
  isSameDay,
  startOfDay,
} from 'date-fns';

import { parseTimeAsTimeValue } from '@dynatrace-sdk/units';
import {
  getRegionalFormat,
  getTimezone,
} from '@dynatrace-sdk/user-preferences';

import {
  applyInitialTime,
  getSecondFractions,
  GRAIL_MAX_DATE,
  GRAIL_MIN_DATE,
} from './date-time.js';
import { removePrecision } from './timeParser.js';
import { getStratoLogger } from '../../../core/sdk/sdk-logger.js';
import { isSimpleTimeOffset } from '../../../core/time/helpers.js';
import type { Precision } from '../../../core/time/shared.types.js';
import type { StringTimeOffset } from '../../../core/time/timeframeStore/calendar.types.js';
import { isOutOfBounds } from '../../../core/time/validation.js';
import { type TimeValue } from '../../../core/types/time.js';
import { isOutsideRange } from '../../../filters/timeframe-selector/utils/timeframe-selection.js';
import type { CalendarState } from '../../calendar/types/shared-types.js';

// Adapted from date-fns.
// In some timezones with DST transitions at midnight (Cairo, Beirut), startOfDay may return 11 PM instead of 12 AM.
function normalizeDates(
  context: ContextFn<Date> | undefined,
  ...dates: Array<DateArg<Date> & {}>
): Date[] {
  const normalize = constructFrom.bind(
    null,
    context || dates.find((date) => typeof date === 'object'),
  );
  return dates.map(normalize);
}

/**
 * Returns an array of dates within the specified time interval.
 *
 * @returns An array with starts of days from the day of the interval start to the day of the interval end
 *
 * @example
 * Each day between 6 October 2014 and 10 October 2014:
 * const result = eachDayOfInterval({
 *   start: new Date(2014, 9, 6),
 *   end: new Date(2014, 9, 10)
 * })
 * => [
 *    Mon Oct 06 2014 00:00:00,
 *    Tue Oct 07 2014 00:00:00,
 *    Wed Oct 08 2014 00:00:00,
 *    Thu Oct 09 2014 00:00:00,
 *    Fri Oct 10 2014 00:00:00
 *  ]
 *
 * @internal
 */
export function eachDayOfInterval(
  interval: { start: TZDate | number | string; end: TZDate | number | string },
  options?: EachDayOfIntervalOptions<TZDate>,
): TZDate[] {
  const [start, end] = normalizeDates(
    options?.in,
    interval.start,
    interval.end,
  );
  let reversed = +start > +end;
  const endTime = reversed ? +start : +end;
  const date = reversed ? end : start;

  date.setHours(0, 0, 0, 0);

  let step = options?.step ?? 1;
  if (!step) {
    return [];
  }
  if (step < 0) {
    step = -step;
    reversed = !reversed;
  }

  const dates: TZDate[] = [];

  while (+date <= endTime) {
    dates.push(constructFrom(start, date));
    date.setHours(date.getHours() + step * 24 + 1);
    date.setHours(0);
  }

  return reversed ? dates.reverse() : dates;
}

/**
 * Sanitizes Timeframe to have a from date that comes before the to date and ensures the times are applied properly.
 * @internal
 */
export function sortTimeframeValues(
  value: { from: TimeValue | null; to: TimeValue | null },
  min?: TimeValue | null,
  max?: TimeValue | null,
  precision?: 'minutes' | 'seconds' | 'milliseconds',
) {
  if (
    value?.from?.absoluteDate &&
    value?.to?.absoluteDate &&
    isBefore(value.to.absoluteDate, value.from.absoluteDate)
  ) {
    // Swaps the `from` and the `to`, to ensure `from` is before `to`.
    // Since the times should be kept on the position, the time value must be reevaluated with the new time offsets.
    return {
      from: getTimeValueFromSimpleValue(
        value.to.value,
        value.from.value,
        'from',
        value.from.type === 'expression',
        min,
        max,
        precision,
      ),
      to: getTimeValueFromSimpleValue(
        value.from.value,
        value.to.value,
        'to',
        value.to.type === 'expression',
        min,
        max,
        precision,
      ),
    };
  }

  return {
    from: value?.from,
    to: value?.to,
  };
}

let hasLoggedSafeRegionalFormatError = false;

export function resetRegionalFormatErrorFlag() {
  hasLoggedSafeRegionalFormatError = false;
}

/**
 * Returns a "safe" regional format string by replacing problematic language codes
 * with 'en' (English) while preserving the region, and by removing any extra subtags.
 * This is used to avoid issues with certain languages or day period formats that may
 * not be supported or may cause formatting errors.
 *
 * - If the language part of the locale is in a known problematic list, it is replaced with 'en'.
 * - If the locale contains more than two subtags (e.g., 'ja-JP-u-ca-japanese'), it is truncated to 'ja-JP'.
 *
 * @internal
 */
export function getSafeRegionalFormat(): string {
  const region = getRegionalFormat();
  const modifiedRegion = removeProblematicLanguagesFromRegion(region);

  try {
    new Intl.Locale(modifiedRegion);
    if (Intl.DisplayNames.supportedLocalesOf([modifiedRegion]).length === 0) {
      throw new Error(
        `Locale "${modifiedRegion}" is not supported by Intl.DisplayNames.`,
      );
    }
    return modifiedRegion;
  } catch (error) {
    /* eslint-disable react-hooks/rules-of-hooks */
    const logger = getStratoLogger('Date-Component');
    if (!hasLoggedSafeRegionalFormatError) {
      logger.error(`Invalid regional format "${region}" encountered.`, error, {
        region,
        modifiedRegion,
      });
      hasLoggedSafeRegionalFormatError = true;
    }
    return 'en-US';
  }
}

const removeProblematicLanguagesFromRegion = (region: string) => {
  const problematicLanguages = [
    // Non-Arabic numerals.
    'ar',
    'as',
    'bgc',
    'bho',
    'bn',
    'ccp',
    'ckb',
    'dz',
    'fa',
    'ff',
    'ks',
    'lrc',
    'mni',
    'mr',
    'my',
    'mzn',
    'ne',
    'pa',
    'ps',
    'raj',
    'sa',
    'sat',
    'sd',
    'ur',
    'uz',
    // Problematic day period.
    'ak',
    'bem',
    'bo',
    'ee',
    'ha',
    'hi',
    'kok',
    'mai',
    'naq',
    'nus',
    'pt',
    'sq',
    'ti',
    'to',
    'yue',
    'zh',
    'tw',
    'bh',
    // Non gregorian calendars.
    'th',
  ];

  let safeRegionalFormat = region;

  if (!safeRegionalFormat) {
    return 'en-US';
  }

  if (safeRegionalFormat.split('-')[1] === undefined) {
    return 'en-' + safeRegionalFormat.split('-')[0];
  }

  // If the language code is problematic, replace it with 'en'.
  if (
    problematicLanguages.find(
      (lang) => lang === safeRegionalFormat.split('-')[0].toLocaleLowerCase(),
    )
  ) {
    safeRegionalFormat = 'en-' + safeRegionalFormat.split('-')[1];
  }

  // If there are more than two subtags, truncate to language-region only.
  // For example, 'ja-JP-u-ca-japanese'
  if (safeRegionalFormat.split('-')[3]) {
    return safeRegionalFormat.split('-').slice(0, 2).join('-');
  }

  return safeRegionalFormat;
};

/**
 * Gets the dayPeriod values such as AM/PM, a.m./p.m. etc. depending on the user's region.
 * @internal
 */
export const getDayPeriodValues = () => {
  const dayPeriod = new Intl.DisplayNames(getSafeRegionalFormat(), {
    type: 'dateTimeField',
  }).of('dayPeriod');

  // Intl does sometimes return just 'Dayperiod' instead of the 'AM/PM'.
  if (!dayPeriod?.split('/')[1]) {
    return ['AM', 'PM'];
  }

  return dayPeriod.split('/');
};

/**
 * parses a timeValue and converts it to a TZDate.
 * @internal
 */
export function timeValueToDate(
  timeValue: TimeValue | undefined,
  relativeDate: number,
): TZDate | null {
  if (!timeValue) {
    return null;
  }
  const parsedString = parseTimeAsTimeValue(timeValue?.value, relativeDate);
  return parsedString?.absoluteDate
    ? new TZDateMini(parsedString.absoluteDate, getTimezone())
    : null;
}

/**
 * Converts a given Date or ISO string to the TimeValue structure for the calendar value.
 * @internal
 */
export const getTimeValueFromSimpleValue = (
  candidate?: string | Date | null,
  timeOffset?: StringTimeOffset,
  position?: CalendarState['position'],
  isExpression?: boolean,
  min?: TimeValue | null,
  max?: TimeValue | null,
  precision?: 'day' | 'minutes' | 'seconds' | 'milliseconds',
): TimeValue | null => {
  if (!candidate) {
    return null;
  }

  const stringDate =
    typeof candidate === 'string' ? candidate : candidate.toISOString();
  const timezone = getTimezone();
  const date = new TZDateMini(stringDate, timezone);

  const minDate = new TZDateMini(
    removePrecision(min?.absoluteDate ?? '', precision),
    timezone,
  );
  // Enforce min boundary if candidate is before min and position is not 'to'.
  if (
    min &&
    position !== 'to' &&
    isOutOfBounds(startOfDay(date), min) &&
    isSameDay(minDate, date)
  ) {
    if (min.type === 'expression') {
      return min;
    }

    const offsetByPrecision = { milliseconds: 0, seconds: 1, minutes: 60 };

    const offsetSeconds =
      // Only add the offset when the min has different precision than the calendar.
      minDate.getTime() === new TZDateMini(min.absoluteDate, timezone).getTime()
        ? 0
        : offsetByPrecision[precision as keyof typeof offsetByPrecision] ?? 0;

    // Add one minute/second depending on the precision to keep the timeValue larger than the min.
    const parsedMin = parseTimeAsTimeValue(
      addSeconds(minDate, offsetSeconds).toISOString(),
      undefined,
      precision,
    );

    if (parsedMin && isSameDay(minDate, date)) {
      return parsedMin;
    }
  }

  // Enforce max boundary if candidate is after max and position is not 'from'.
  if (
    max &&
    position !== 'from' &&
    isOutOfBounds(endOfDay(date), undefined, max) &&
    isSameDay(new TZDateMini(max.absoluteDate, timezone), date)
  ) {
    return max;
  }

  const parsedDate = parseTimeAsTimeValue(stringDate, undefined, precision);
  if (!parsedDate) {
    return null;
  }

  const isRelativeExpression = isExpression || parsedDate.type === 'expression';

  // Preserve the original absoluteDate (including its time component) when precision
  // is 'day'. The time is used internally to resolve the correct calendar date once precision is applied.
  const resolvedDate =
    precision === 'day'
      ? parsedDate.absoluteDate
      : _getPreciseIsoString(
          parsedDate.absoluteDate,
          timeOffset,
          position,
          isRelativeExpression,
        );

  return isRelativeExpression
    ? { ...parsedDate, absoluteDate: resolvedDate, type: 'expression' }
    : { ...parsedDate, absoluteDate: resolvedDate, value: resolvedDate };
};

/** Applies the proper timeoffset and returns the date as ISO String. */
function _getPreciseIsoString(
  isoString: string,
  timeOffset?: StringTimeOffset,
  position?: CalendarState['position'],
  isExpression?: boolean,
) {
  if (!timeOffset) {
    return isoString;
  }

  const offsetDate = isSimpleTimeOffset(timeOffset)
    ? timeOffset
    : timeOffset[position ?? 'from'];

  const date = new TZDateMini(isoString, getTimezone());

  if (isExpression) {
    return date.toISOString();
  }

  switch (offsetDate) {
    case 'startOfDay':
      return startOfDay(date).toISOString();
    case 'endOfDay':
      return endOfDay(date).toISOString();
    default: {
      if (isoString === offsetDate) {
        return isoString;
      }

      const parsedOffsetDate = parseTimeAsTimeValue(offsetDate);

      if (parsedOffsetDate?.type === 'expression') {
        return parsedOffsetDate.absoluteDate;
      }

      const precision = getSecondFractions(offsetDate);
      return applyInitialTime(isoString, offsetDate, precision);
    }
  }
}

/**
 * For 'date' type, it adjusts the time by the timezone offset to ensure the
 * date is correct without time information.
 * For 'time' and 'datetime' types, it returns the ISO string as is.
 */
export const convertDateOnlyToDateTime = (
  isoString: string | undefined,
  type: 'time' | 'date' | 'datetime' | undefined,
): string => {
  if (type === 'date') {
    const newDate = new TZDateMini(isoString ?? '', getTimezone());
    if (!isNaN(newDate.getTime())) {
      newDate.setMinutes(newDate.getMinutes() + newDate.getTimezoneOffset());
      return newDate.toISOString();
    }
  }
  return isoString ?? '';
};

/**
 * Converts a raw TimeValue to the correct calendarValue shape.
 * When precision is 'day', the absoluteDate/value fields must contain a full
 * ISO datetime string (produced by convertDateOnlyToDateTime) rather than the
 * date-only string stored in dateTimePickerValue.
 */
export function toCalendarValue(
  value: TimeValue | null | undefined,
  precision: Precision | undefined,
): TimeValue | null {
  if (!value || value.absoluteDate === '') {
    return null;
  }
  if (precision === 'day') {
    const converted = convertDateOnlyToDateTime(value.absoluteDate, 'date');
    if (converted) {
      return { absoluteDate: converted, value: converted, type: 'iso8601' };
    }
  }
  return value;
}

/**
 * Filters the Grail min or max date.
 * Returns `undefined` if the provided limit is the default Grail min or max and the value is not outside the Grail range.
 * Otherwise, returns the custom min or max limit.
 * @internal
 */
export function filterOutGrailLimit(
  value: {
    from: string | null | TimeValue;
    to: string | null | TimeValue;
  } | null,
  limit?: string,
): string | undefined {
  const isOutsideGrailLimit = isOutsideRange(
    value,
    GRAIL_MIN_DATE,
    GRAIL_MAX_DATE,
  );

  return (limit === GRAIL_MIN_DATE || limit === GRAIL_MAX_DATE) &&
    !isOutsideGrailLimit
    ? undefined
    : limit;
}
